import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents } from "../../api/studentApi";
import "./Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const daysInMonth = (year, monthName) => new Date(year, MONTHS.indexOf(monthName) + 1, 0).getDate();

/**
 * Direct port of digicampusbd's `student-attendance-blank-sheet`
 * ("Attendance Sheet"). Confirmed via `StudentReportService
 * .studentGeneralListBySectionGroupAndCategoryList` that the old app's
 * backend contributes only the plain student roster (filtered by class +
 * group + a multi-select category list) — it has **zero knowledge of
 * "attendance" or "month"** at all; the blank day-grid is built entirely in
 * client JS for manual/printed marking. **Zero new backend** — reuses the
 * already-exposed `GET /api/students` (filtered client-side by group and
 * chosen categories, matching the old app's own multi-select category
 * filter, since `StudentResponse` already carries `groupId`/`categoryId`).
 */
export default function StudentAttendanceBlankSheetPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [monthName, setMonthName] = useState(MONTHS[new Date().getMonth()]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then(setGroups);
    categoryApi.list().then((list) => {
      setCategories(list);
      setCategoryIds(list.map((c) => String(c.id)));
    });
  }, []);

  const toggleCategory = (id) => setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    if (!academicYear || !classConfigId) {
      setError("Please select academic year and section.");
      return;
    }
    try {
      const students = await listStudents(academicYear, classConfigId);
      const filtered = students.filter(
        (s) => (!groupId || String(s.groupId) === String(groupId)) && (categoryIds.length === 0 || categoryIds.includes(String(s.categoryId)))
      );
      setRows(filtered.sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0)));
    } catch (err) {
      setError("Failed to load student list.");
    }
  };

  const dayCols = Array.from({ length: daysInMonth(year, monthName) }, (_, i) => String(i + 1));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Attendance Blank Sheet</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Month
          <select value={monthName} onChange={(e) => setMonthName(e.target.value)}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      <div className="form-row">
        <span className="admit-card-label">Categories:</span>{" "}
        {categories.map((c) => (
          <label key={c.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={categoryIds.includes(String(c.id))} onChange={() => toggleCategory(String(c.id))} />
            {c.name}
          </label>
        ))}
      </div>
      {rows && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Student Name</th>
                <th>Roll</th>
                {dayCols.map((d) => (
                  <th key={d}></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.identificationId}>
                  <td>{r.customStudentId}</td>
                  <td>{r.studentName}</td>
                  <td>{r.studentRoll}</td>
                  {dayCols.map((d) => (
                    <td key={d}></td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={dayCols.length + 3}>No students found for this selection.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </MainLayout>
  );
}
